import {
  computePackage,
  type ComputeResult,
  type FormulaCharge,
  type LineItemLike,
  type Package,
} from '@everplan/shared/lib/charge-calc';
import { normalizeSection } from '@everplan/shared/lib/section-aliases';
import type { Basis, Confidence, InferenceSource } from '@everplan/shared/types/charge';

export type { ComputeResult, FormulaCharge, LineItemLike, Package };

/**
 * Form-shaped line item — what `EditorForm`/`PackageCard` carry.
 * `quantity` is optional in the form input but the engine wants `number | null`.
 */
export interface FormLineItem {
  unitCost: number;
  quantity?: number | null;
  section?: string | null;
}

/**
 * Form-shaped charge. `calcMethod`/`rateBps`/`basis` are optional; absent means
 * "flat" (the persisted default). The optional `id` lets callers pass real
 * persisted UUIDs through so cross-charge basis references resolve correctly;
 * if absent we synthesize a stable index-based id.
 */
export interface FormCharge {
  id?: string | null;
  amount: number;
  calcMethod?: 'flat' | 'percentage';
  rateBps?: number | null;
  basis?: Basis | null;
  inferenceSource?: InferenceSource;
  confidence?: Confidence | null;
}

export interface FormPackage {
  lineItems: FormLineItem[];
  additionalCharges: FormCharge[];
  selectedAddOns?: FormLineItem[];
}

/**
 * Map a form-shaped package onto the calc engine's `Package`. Pure shape
 * adapter — no math, no IO. Use the result with `computePackageDetails`.
 */
export function toEnginePackage(pkg: FormPackage): Package {
  const lineItems: LineItemLike[] = pkg.lineItems.map((item) => ({
    unitCost: item.unitCost,
    quantity: item.quantity ?? null,
    sectionNormalized: normalizeSection(item.section ?? null),
  }));
  const selectedAddOns: LineItemLike[] = (pkg.selectedAddOns ?? []).map((ao) => ({
    unitCost: ao.unitCost,
    quantity: ao.quantity ?? null,
    sectionNormalized: normalizeSection(ao.section ?? null),
  }));
  const charges: FormulaCharge[] = pkg.additionalCharges.map((c, idx) => ({
    id: c.id ?? `form-charge-${idx}`,
    calcMethod: c.calcMethod ?? 'flat',
    rateBps: c.rateBps ?? null,
    basis: c.basis ?? null,
    amount: c.amount,
  }));
  return { lineItems, charges, selectedAddOns };
}

export interface ReconcileInputPackage {
  lineItems: Array<{ unitCost: number; quantity?: number | null }>;
  additionalCharges: Array<{ amount: number }>;
}

export type ReconcileStatus = 'match' | 'mismatch' | 'unverified';

export interface ReconcileResult {
  status: ReconcileStatus;
  computed: number;
  stated: number | null;
  diff: number;
  tolerance: number;
}

/**
 * Thin shim that delegates to the shared `computePackage` engine.
 *
 * Existing call sites pass a minimal package shape — line items with
 * `unitCost`/`quantity` and charges with just `amount`. This adapter maps that
 * shape onto the engine's `Package` (every charge becomes a flat charge with a
 * synthesized id, every line item becomes a sectionless `LineItemLike`).
 *
 * For flat-only packages this produces identical results to the old hand-rolled
 * sum. New callers that need the full picture (per-charge resolved amounts,
 * percentage charges, errors) should call `computePackageDetails` directly.
 */
export function computePackageTotal(pkg: ReconcileInputPackage): number {
  const adapter: Package = {
    lineItems: pkg.lineItems.map((item) => ({
      unitCost: item.unitCost,
      quantity: item.quantity ?? null,
      sectionNormalized: null,
    })),
    charges: pkg.additionalCharges.map((c, idx) => ({
      id: `adapter-${idx}`,
      calcMethod: 'flat',
      rateBps: null,
      basis: null,
      amount: c.amount,
    })),
    selectedAddOns: [],
  };
  return computePackage(adapter).packageTotal;
}

/**
 * Direct passthrough to the shared calc engine. Prefer this over
 * `computePackageTotal` when the caller has formula-bearing charges
 * (`calcMethod: 'percentage'`) or needs per-charge resolved amounts and errors.
 */
export function computePackageDetails(pkg: Package): ComputeResult {
  return computePackage(pkg);
}

export function sumCharges(charges: Array<{ amount: number }>): number {
  return charges.reduce((sum, c) => sum + c.amount, 0);
}

export function reconcileStatedTotal(stated: number | null, computed: number): ReconcileResult {
  if (stated == null) {
    return { status: 'unverified', computed, stated, diff: 0, tolerance: 0 };
  }
  const diff = computed - stated;
  return {
    status: diff === 0 ? 'match' : 'mismatch',
    computed,
    stated,
    diff,
    tolerance: 0,
  };
}
